import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/services/api';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { 
  FolderKanban, 
  Plus, 
  Edit, 
  Trash2, 
  User, 
  Calendar, 
  DollarSign, 
  Users,
  CheckCircle,
  XCircle,
  Clock,
  Play,
  Pause,
  Eye,
  Settings,
  TrendingUp,
  BarChart3
} from 'lucide-react';
import { Project, User as UserType, ProjectFinancialSummary } from '@/types';

const Projects: React.FC = () => {
  const { user } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [users, setUsers] = useState<UserType[]>([]);
  const [loading, setLoading] = useState(true);
  const [financialSummaries, setFinancialSummaries] = useState<Record<number, ProjectFinancialSummary>>({});
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [message, setMessage] = useState<{
    type: 'success' | 'error' | null;
    text: string;
  }>({ type: null, text: '' });

  const [projectForm, setProjectForm] = useState({
    name: '',
    description: '',
    status: 'not_started' as const,
    start_date: '',
    end_date: '',
    budget: '',
    manager_id: '',
  });

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchProjects();
      fetchUsers();
    }
  }, [user?.role]); // 只依赖用户角色，而不是整个user对象

  const fetchProjects = async () => {
    try {
      const projectsData = await api.getProjects();
      setProjects(projectsData);
      
      // 加载每个项目的财务汇总数据
      const summaries: Record<number, ProjectFinancialSummary> = {};
      for (const project of projectsData) {
        try {
          const summary = await api.getProjectFinancialSummary(project.id);
          summaries[project.id] = summary;
        } catch (error) {
          console.error(`Failed to fetch financial summary for project ${project.id}:`, error);
        }
      }
      setFinancialSummaries(summaries);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const usersData = await api.getUsers();
      // 只显示已批准的用户作为可选的项目负责人
      setUsers(usersData.filter(u => u.status === 'approved'));
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const resetForm = () => {
    setProjectForm({
      name: '',
      description: '',
      status: 'not_started',
      start_date: '',
      end_date: '',
      budget: '',
      manager_id: '',
    });
    setShowCreateForm(false);
    setEditingProject(null);
  };

  const handleCreateProject = async () => {
    if (!projectForm.name.trim()) {
      setMessage({ type: 'error', text: '项目名称不能为空' });
      setTimeout(() => setMessage({ type: null, text: '' }), 3000);
      return;
    }

    try {
      const projectData = {
        ...projectForm,
        budget: projectForm.budget ? Number(projectForm.budget) : undefined,
        manager_id: projectForm.manager_id ? Number(projectForm.manager_id) : undefined,
        start_date: projectForm.start_date || undefined,
        end_date: projectForm.end_date || undefined,
      };

      await api.createProject(projectData);
      await fetchProjects();
      resetForm();
      setMessage({ type: 'success', text: '项目创建成功！' });
      setTimeout(() => setMessage({ type: null, text: '' }), 3000);
    } catch (error: any) {
      console.error('Failed to create project:', error);
      setMessage({ 
        type: 'error', 
        text: `创建项目失败: ${error.response?.data?.detail || error.message}` 
      });
      setTimeout(() => setMessage({ type: null, text: '' }), 5000);
    }
  };

  const handleUpdateProject = async () => {
    if (!editingProject || !projectForm.name.trim()) {
      setMessage({ type: 'error', text: '项目名称不能为空' });
      setTimeout(() => setMessage({ type: null, text: '' }), 3000);
      return;
    }

    try {
      const projectData = {
        ...projectForm,
        budget: projectForm.budget ? Number(projectForm.budget) : undefined,
        manager_id: projectForm.manager_id ? Number(projectForm.manager_id) : undefined,
        start_date: projectForm.start_date || undefined,
        end_date: projectForm.end_date || undefined,
      };

      await api.updateProject(editingProject.id, projectData);
      await fetchProjects();
      resetForm();
      setMessage({ type: 'success', text: '项目更新成功！' });
      setTimeout(() => setMessage({ type: null, text: '' }), 3000);
    } catch (error: any) {
      console.error('Failed to update project:', error);
      setMessage({ 
        type: 'error', 
        text: `更新项目失败: ${error.response?.data?.detail || error.message}` 
      });
      setTimeout(() => setMessage({ type: null, text: '' }), 5000);
    }
  };

  const handleDeleteProject = async (projectId: number, projectName: string) => {
    if (!window.confirm(`确定要删除项目 "${projectName}" 吗？此操作不可撤销。`)) {
      return;
    }

    try {
      await api.deleteProject(projectId);
      await fetchProjects();
      setMessage({ type: 'success', text: '项目删除成功！' });
      setTimeout(() => setMessage({ type: null, text: '' }), 3000);
    } catch (error: any) {
      console.error('Failed to delete project:', error);
      setMessage({ 
        type: 'error', 
        text: `删除项目失败: ${error.response?.data?.detail || error.message}` 
      });
      setTimeout(() => setMessage({ type: null, text: '' }), 5000);
    }
  };

  const handleUpdateStatus = async (projectId: number, newStatus: string) => {
    try {
      await api.updateProjectStatus(projectId, newStatus);
      await fetchProjects();
      setMessage({ type: 'success', text: '项目状态更新成功！' });
      setTimeout(() => setMessage({ type: null, text: '' }), 3000);
    } catch (error: any) {
      console.error('Failed to update project status:', error);
      setMessage({ 
        type: 'error', 
        text: `更新项目状态失败: ${error.response?.data?.detail || error.message}` 
      });
      setTimeout(() => setMessage({ type: null, text: '' }), 5000);
    }
  };

  const handleUpdateManager = async (projectId: number, managerId: number | null) => {
    try {
      await api.updateProjectManager(projectId, managerId);
      await fetchProjects();
      setMessage({ type: 'success', text: '项目负责人更新成功！' });
      setTimeout(() => setMessage({ type: null, text: '' }), 3000);
    } catch (error: any) {
      console.error('Failed to update project manager:', error);
      setMessage({ 
        type: 'error', 
        text: `更新项目负责人失败: ${error.response?.data?.detail || error.message}` 
      });
      setTimeout(() => setMessage({ type: null, text: '' }), 5000);
    }
  };

  const startEdit = (project: Project) => {
    setEditingProject(project);
    setProjectForm({
      name: project.name,
      description: project.description || '',
      status: project.status,
      start_date: project.start_date || '',
      end_date: project.end_date || '',
      budget: project.budget?.toString() || '',
      manager_id: project.manager_id?.toString() || '',
    });
    setShowCreateForm(true);
  };

  const getStatusLabel = (status: string) => {
    const labels = {
      not_started: '未开始',
      in_progress: '进行中',
      completed: '已完成',
    };
    return labels[status as keyof typeof labels] || status;
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'not_started':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          <Clock className="w-3 h-3 mr-1" />
          未开始
        </span>;
      case 'in_progress':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          <Play className="w-3 h-3 mr-1" />
          进行中
        </span>;
      case 'completed':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle className="w-3 h-3 mr-1" />
          已完成
        </span>;
      default:
        return status;
    }
  };

  const getUserName = (userId: number | null) => {
    if (!userId) return null;
    const user = users.find(u => u.id === userId);
    return user ? (user.real_name || user.username) : '用户不存在';
  };

  if (user?.role !== 'admin') {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold text-gray-900">项目管理</h1>
        
        <Card>
          <Card.Content className="p-6 text-center">
            <FolderKanban className="h-12 w-12 mx-auto mb-4 text-red-300" />
            <p className="text-lg font-medium mb-2">访问受限</p>
            <p className="text-sm text-gray-500">只有管理员可以访问项目管理页面</p>
          </Card.Content>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold text-gray-900">项目管理</h1>
        <Card>
          <Card.Content className="p-6 text-center">
            <p>加载中...</p>
          </Card.Content>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 消息提示 */}
      {message.type && (
        <div className={`p-4 rounded-md ${
          message.type === 'success' 
            ? 'bg-green-50 text-green-800 border border-green-200' 
            : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          <div className="flex items-center">
            {message.type === 'success' ? (
              <CheckCircle className="h-4 w-4 mr-2" />
            ) : (
              <XCircle className="h-4 w-4 mr-2" />
            )}
            {message.text}
          </div>
        </div>
      )}

      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">项目管理</h1>
        <Button onClick={() => setShowCreateForm(true)}>
          <Plus className="w-4 h-4 mr-2" />
          创建项目
        </Button>
      </div>

      {/* 创建/编辑项目表单 */}
      {showCreateForm && (
        <Card>
          <Card.Header>
            <Card.Title>{editingProject ? '编辑项目' : '创建新项目'}</Card.Title>
          </Card.Header>
          <Card.Content>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  项目名称 *
                </label>
                <Input
                  value={projectForm.name}
                  onChange={(e) => setProjectForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="输入项目名称"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  项目状态
                </label>
                <select
                  value={projectForm.status}
                  onChange={(e) => setProjectForm(prev => ({ ...prev, status: e.target.value as any }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="not_started">未开始</option>
                  <option value="in_progress">进行中</option>
                  <option value="completed">已完成</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  项目描述
                </label>
                <textarea
                  value={projectForm.description}
                  onChange={(e) => setProjectForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="输入项目描述"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  开始日期
                </label>
                <Input
                  type="date"
                  value={projectForm.start_date}
                  onChange={(e) => setProjectForm(prev => ({ ...prev, start_date: e.target.value }))}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  结束日期
                </label>
                <Input
                  type="date"
                  value={projectForm.end_date}
                  onChange={(e) => setProjectForm(prev => ({ ...prev, end_date: e.target.value }))}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  项目预算 (¥)
                </label>
                <Input
                  type="number"
                  value={projectForm.budget}
                  onChange={(e) => setProjectForm(prev => ({ ...prev, budget: e.target.value }))}
                  placeholder="输入项目预算"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  项目负责人
                </label>
                <select
                  value={projectForm.manager_id}
                  onChange={(e) => setProjectForm(prev => ({ ...prev, manager_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="">选择负责人</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.real_name || user.username} ({user.email})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex space-x-2 mt-6">
              <Button onClick={editingProject ? handleUpdateProject : handleCreateProject}>
                {editingProject ? '更新项目' : '创建项目'}
              </Button>
              <Button variant="outline" onClick={resetForm}>
                取消
              </Button>
            </div>
          </Card.Content>
        </Card>
      )}

      {/* 项目列表 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {projects.map((project) => (
          <Card key={project.id}>
            <Card.Header className="flex justify-between items-start">
              <div className="flex-1">
                <Card.Title className="text-lg">{project.name}</Card.Title>
                <div className="mt-2">{getStatusBadge(project.status)}</div>
              </div>
              <div className="flex space-x-1 ml-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => startEdit(project)}
                  className="p-1"
                >
                  <Edit className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => handleDeleteProject(project.id, project.name)}
                  className="p-1"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </Card.Header>
            <Card.Content>
              {project.description && (
                <p className="text-gray-600 mb-3 text-sm">{project.description}</p>
              )}
              
              <div className="space-y-2 text-sm">
                <div className="flex items-center">
                  <User className="w-4 h-4 mr-2" />
                  <span>负责人: </span>
                  {project.manager_id ? (
                    (() => {
                      const managerName = getUserName(project.manager_id);
                      return managerName === '用户不存在' ? (
                        <span className="text-red-500">{managerName}</span>
                      ) : (
                        <span className="text-gray-600">{managerName}</span>
                      );
                    })()
                  ) : (
                    <span className="text-red-500">暂无负责人</span>
                  )}
                </div>
                
                {project.budget && (
                  <div className="flex items-center text-gray-600">
                    <DollarSign className="w-4 h-4 mr-2" />
                    预算: ¥{project.budget.toLocaleString()}
                  </div>
                )}
                
                {project.start_date && (
                  <div className="flex items-center text-gray-600">
                    <Calendar className="w-4 h-4 mr-2" />
                    开始: {new Date(project.start_date).toLocaleDateString()}
                  </div>
                )}
                
                {project.end_date && (
                  <div className="flex items-center text-gray-600">
                    <Calendar className="w-4 h-4 mr-2" />
                    结束: {new Date(project.end_date).toLocaleDateString()}
                  </div>
                )}

                <div className="flex items-center text-gray-600">
                  <Users className="w-4 h-4 mr-2" />
                  成员: {project.member_count || 0} 人
                </div>

                {/* 财务信息 */}
                {financialSummaries[project.id] && (
                  <>
                    <div className="flex items-center text-green-600">
                      <TrendingUp className="w-4 h-4 mr-2" />
                      收入: ¥{financialSummaries[project.id].total_revenue.toLocaleString()}
                    </div>
                    
                    <div className="flex items-center text-blue-600">
                      <BarChart3 className="w-4 h-4 mr-2" />
                      利润: ¥{financialSummaries[project.id].net_profit.toLocaleString()}
                      {financialSummaries[project.id].profit_margin !== 0 && (
                        <span className="ml-1 text-xs">
                          ({financialSummaries[project.id].profit_margin.toFixed(1)}%)
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* 快速操作按钮 */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex flex-wrap gap-2">
                  {project.status !== 'in_progress' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleUpdateStatus(project.id, 'in_progress')}
                    >
                      <Play className="w-3 h-3 mr-1" />
                      开始
                    </Button>
                  )}
                  
                  {project.status !== 'completed' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleUpdateStatus(project.id, 'completed')}
                    >
                      <CheckCircle className="w-3 h-3 mr-1" />
                      完成
                    </Button>
                  )}

                  <select
                    value={project.manager_id || ''}
                    onChange={(e) => handleUpdateManager(project.id, e.target.value ? Number(e.target.value) : null)}
                    className="text-xs px-2 py-1 border border-gray-300 rounded"
                  >
                    <option value="">指定负责人</option>
                    {users.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.real_name || user.username}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </Card.Content>
          </Card>
        ))}
      </div>

      {projects.length === 0 && (
        <Card>
          <Card.Content className="p-6 text-center">
            <FolderKanban className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-medium mb-2">暂无项目</p>
            <p className="text-sm text-gray-500 mb-4">创建您的第一个项目开始管理</p>
            <Button onClick={() => setShowCreateForm(true)}>
              <Plus className="w-4 h-4 mr-2" />
              创建项目
            </Button>
          </Card.Content>
        </Card>
      )}
    </div>
  );
};

export default Projects;